<script lang="ts">
	import type { Notification } from '$lib/types/notifications';
	import GradientBorder from '$lib/util/GradientBorder.svelte';
	import { optimism } from '@wagmi/core/chains';
	import { hideNotification } from './notificationManager';
	import _ from 'lodash';
	export let notif: Notification;
	export let notifId: string;
	import CopyToClipboard from 'svelte-copy-to-clipboard';
	import { fly } from 'svelte/transition';

	let COPY_ERROR_MESSAGE = 'COPY ERROR';
	function handleSuccessCopyError() {
		COPY_ERROR_MESSAGE = 'COPIED';
		setTimeout(() => {
			COPY_ERROR_MESSAGE = 'COPY';
		}, 2000);
	}

	let showMore = false;
</script>

{#if notif.type == 'Success'}
	<div
		in:fly|global={{ x: 200, duration: 500, delay: 1000 }}
		out:fly|global={{ x: 200, duration: 500, delay: 500 }}
		class="absolute bottom-0 w-full"
	>
		<GradientBorder
			gradient="bg-fx-gradient-23-50"
			paddingClass="p-px"
			borderRadius="rounded-2xl squircle-2xl"
			outerClassList="w-full my-4"
			innerClassList="flex justify-center items-center bg-fx-black text-fx-white w-full"
		>
			<div class="bg-fx-gradient-8 p-4 rounded-2xl squircle-2xl w-full">
				<div class="flex justify-between gap-10">
					<div>
						<div class="text-fx-gradient-5 text-sm" data-testid="success-notif-title">
							{notif.title}
						</div>
						<div class="text-fx-grey-300 text-sm">
							{notif.description}
						</div>
					</div>
					{#if notif.transaction}
						<a
							href={`${optimism.blockExplorers?.default.url}/tx/${notif.transaction.hash}`}
							target="_blank"
							rel="noreferrer"
						>
							<img
								loading="lazy"
								src={'link-grey.png'}
								alt="etherscan"
								class="cursor-pointer w-5 h-5"
							/>
						</a>
					{/if}
				</div>
				<div class="border-t border-t-fx-grey-600 my-4" />
				<div class="flex justify-between">
					<div
						on:click={() => {
							hideNotification(notifId);
						}}
						on:keydown={() => {
							hideNotification(notifId);
						}}
						class="text-xs font-medium text-fx-grey-400 cursor-pointer"
						role="presentation"
					>
						Hide Notification
					</div>

					<div class="text-fx-gradient-23 text-xs font-medium">Completed</div>
				</div>
			</div>
		</GradientBorder>
	</div>
{:else if notif.type == 'Error'}
	<div
		in:fly|global={{ x: 200, duration: 500, delay: 1000 }}
		out:fly|global={{ x: 200, duration: 500, delay: 500 }}
		class="absolute bottom-0 w-full"
	>
		<GradientBorder
			gradient="bg-fx-gradient-22-50"
			paddingClass="p-px"
			borderRadius="rounded-2xl squircle-2xl"
			outerClassList="w-full my-4"
			innerClassList="flex justify-center items-center bg-fx-black text-fx-white w-full"
		>
			<div class="bg-fx-gradient-8 p-4 rounded-2xl squircle-2xl w-full">
				<div class="flex justify-between gap-10 w-full">
					<div class="w-full">
						<div class="text-fx-gradient-5 text-sm w-full max-h-44">
							{_.startCase(notif.title)}
						</div>
						<div class="text-fx-grey-300 text-sm w-full max-h-44 overflow-auto scrollbar-hide">
							{notif.description}
						</div>
						<!-- Show Full Error -->
						<div
							class="text-fx-grey-300 text-sm font-semibold flex items-center cursor-pointer"
							on:click={() => (showMore = !showMore)}
							on:keydown={() => (showMore = !showMore)}
							role="presentation"
						>
							<div class="h-2 pr-2">
								<img
									loading="lazy"
									src={'down-arrow.png'}
									alt=""
									class="w-2.5 transition-transform duration-300 ease-in-out"
									class:flip-arrow={showMore}
								/>
							</div>
							<div>Full Error</div>
						</div>
						<div
							class="cursor-pointer overflow-auto scrollbar-hide max-h-44 text-fx-grey-300 text-sm"
							on:click={() => (showMore = !showMore)}
							on:keydown={() => (showMore = !showMore)}
							role="presentation"
						>
							{#if showMore}
								{notif.fullError}
							{/if}
						</div>
					</div>
					{#if notif.transaction}
						<a
							href={`${optimism.blockExplorers?.default.url}/tx/${notif.transaction.hash}`}
							target="_blank"
							rel="noreferrer"
						>
							<img
								loading="lazy"
								src={'link-grey.png'}
								alt="etherscan"
								class="cursor-pointer w-5 h-5"
							/>
						</a>
					{/if}
				</div>
				<div class="border-t border-t-fx-grey-600 my-4" />
				<div class="flex justify-between">
					<div
						on:click={() => {
							hideNotification(notifId);
						}}
						on:keydown={() => {
							hideNotification(notifId);
						}}
						class="text-xs font-medium text-fx-grey-400 cursor-pointer"
						role="presentation"
					>
						Hide Notification
					</div>

					<CopyToClipboard
						text={notif.fullError}
						on:copy={() => {
							handleSuccessCopyError();
						}}
						on:fail={() => {
							alert('Failed to copy. Please copy manually: ' + notif.fullError);
						}}
						let:copy
					>
						<div
							class="flex items-center cursor-pointer text-xs text-fx-grey-300"
							on:click={copy}
							on:keydown={copy}
							role="presentation"
						>
							{COPY_ERROR_MESSAGE}
						</div>
					</CopyToClipboard>
				</div>
			</div>
		</GradientBorder>
	</div>
{:else if notif.type == 'Pending'}
	<div
		in:fly|global={{ x: 200, duration: 500, delay: 1000 }}
		out:fly|global={{ x: 200, duration: 500, delay: 500 }}
		class="absolute bottom-0 w-full"
	>
		<GradientBorder
			gradient="bg-fx-gradient-9-50"
			paddingClass="p-px"
			borderRadius="rounded-2xl squircle-2xl"
			outerClassList="w-full my-4"
			innerClassList="flex justify-center items-center bg-fx-black text-fx-white w-full"
		>
			<div class="bg-fx-gradient-8 p-4 rounded-2xl squircle-2xl w-full">
				<div class="flex justify-between gap-10">
					<div>
						<div class="text-fx-gradient-5 text-sm" data-testid="pending-notif-title">
							{notif.title}
						</div>
						<div class="text-fx-grey-300 text-sm">
							{notif.description}
						</div>
					</div>
					{#if notif.transaction}
						<a
							href={`${optimism.blockExplorers?.default.url}/tx/${notif.transaction.hash}`}
							target="_blank"
							rel="noreferrer"
						>
							<img
								loading="lazy"
								src={'link-grey.png'}
								alt="etherscan"
								class="cursor-pointer w-5 h-5"
							/>
						</a>
					{/if}
				</div>
				<div class="border-t border-t-fx-grey-600 my-4" />
				<div class="flex justify-between">
					<div
						on:click={() => {
							hideNotification(notifId);
						}}
						on:keydown={() => {
							hideNotification(notifId);
						}}
						role="presentation"
						class="text-xs font-medium text-fx-grey-400 cursor-pointer"
					>
						Hide Notification
					</div>

					<div class="text-fx-gradient-9 text-xs font-medium">Pending</div>
				</div>
			</div>
		</GradientBorder>
	</div>
{/if}

<style>
	.flip-arrow {
		transform: rotate(180deg);
	}
	/* For Webkit-based browsers (Chrome, Safari and Opera) */
	.scrollbar-hide::-webkit-scrollbar {
		display: none;
	}

	/* For IE, Edge and Firefox */
	.scrollbar-hide {
		-ms-overflow-style: none; /* IE and Edge */
		scrollbar-width: none; /* Firefox */
	}
</style>
